import type { ChannelAgent, SetChannelAgent } from "@/services/conversations.api";

/** Lo que elige el admin en la ficha: `off` es el asistente guardado pero apagado. */
export type ModoDelCanal = "off" | "assist" | "auto";

export const MODOS: { value: ModoDelCanal; label: string; ayuda: string }[] = [
  { value: "off", label: "Apagado", ayuda: "No lee ni responde en este canal." },
  {
    value: "assist",
    label: "Copiloto",
    ayuda: "Le contesta al operador en notas internas cuando lo mencionan. Al cliente no le escribe solo.",
  },
  {
    value: "auto",
    label: "Automático",
    ayuda: "Le responde al cliente por WhatsApp y deriva a una persona cuando hace falta.",
  },
];

export function modoDe(agent: ChannelAgent | null): ModoDelCanal {
  if (!agent || !agent.enabled) return "off";
  return agent.mode;
}

/** `auto` sólo en la Cloud API (D23): la API lo rechaza igual con `AUTO_REQUIRES_CLOUD`. */
export function modoPermitido(modo: ModoDelCanal, esCloud: boolean): boolean {
  return modo !== "auto" || esCloud;
}

export type Guardado =
  | { accion: "nada" }
  | { accion: "error"; motivo: string }
  | { accion: "guardar"; body: SetChannelAgent };

/**
 * Qué hacer al guardar. Apagar no borra el asistente: lo deja con `enabled: false` y su modo, así al
 * prenderlo vuelve como estaba. Sin asistente guardado, apagado no hay nada que hacer.
 */
export function guardadoDelAgente(
  modo: ModoDelCanal,
  elegido: { userId: number | null; displayName: string; sellerCode?: string | null },
  actual: ChannelAgent | null,
  esCloud: boolean,
): Guardado {
  if (modo === "off" && !actual) return { accion: "nada" };
  if (!modoPermitido(modo, esCloud))
    return { accion: "error", motivo: "El modo automático es sólo para canales de WhatsApp Cloud API." };
  const userId = elegido.userId ?? actual?.userId ?? null;
  if (!userId) return { accion: "error", motivo: "Elegí el usuario del asistente." };
  const displayName = elegido.displayName.trim() || actual?.displayName || "";
  if (!displayName) return { accion: "error", motivo: "Poné el nombre con el que firma." };
  const body: SetChannelAgent = {
    userId,
    displayName,
    mode: modo === "off" ? (actual?.mode ?? "assist") : modo,
    enabled: modo !== "off",
    // D58: vacío es `null`, por el store. Sin el campo en `elegido`, se conserva el que tenía.
    sellerCode: elegido.sellerCode === undefined ? (actual?.sellerCode ?? null) : elegido.sellerCode || null,
  };
  if (actual) {
    body.sendRatePerMin = actual.sendRatePerMin;
    body.debounceSeconds = actual.debounceSeconds;
  }
  if (
    actual &&
    actual.userId === body.userId &&
    actual.displayName === body.displayName &&
    actual.mode === body.mode &&
    actual.enabled === body.enabled &&
    (actual.sellerCode ?? null) === (body.sellerCode ?? null)
  )
    return { accion: "nada" };
  return { accion: "guardar", body };
}

/** Quién administra el asistente del canal: el mismo gate que el conversation-service. */
export function esAdminDelStore(identity: any, storeCode: string): boolean {
  return (
    String(identity?.permissions?.store?.store_permission?.[storeCode] ?? "")
      .trim()
      .toLowerCase() === "admin"
  );
}
